import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, getDocs, addDoc, deleteDoc, doc } from 'firebase/firestore';
import './CostoEnvio.css';
import Notification from './Notification';
import Spinner from './Spinner';

const CostoEnvio = () => {
  const [shippingCosts, setShippingCosts] = useState([]);
  const [shippingDiscounts, setShippingDiscounts] = useState([]);
  const [formType, setFormType] = useState(''); // '' | 'cost' | 'discount'
  const [minQuantity, setMinQuantity] = useState('');
  const [shippingCost, setShippingCost] = useState('');
  const [minAmount, setMinAmount] = useState('');
  const [shippingDiscount, setShippingDiscount] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState({ message: '', show: false });

  useEffect(() => {
    const fetchShippingData = async () => {
      setIsLoading(true);
      try {
        // Cargar costos de envío básicos
        const shippingCostsSnapshot = await getDocs(collection(db, 'shippingCosts'));
        const fetchedShippingCosts = shippingCostsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setShippingCosts(fetchedShippingCosts);

        // Cargar descuentos de envío
        const shippingDiscountsSnapshot = await getDocs(collection(db, 'shippingDiscounts'));
        const fetchedShippingDiscounts = shippingDiscountsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setShippingDiscounts(fetchedShippingDiscounts);

        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
        showNotification('Error al cargar la configuración de envío.');
        console.error("Error fetching shipping data: ", error);
      }
    };

    fetchShippingData();
  }, []);

  const showNotification = (message) => {
    setNotification({ message, show: true });
    setTimeout(() => setNotification({ message: '', show: false }), 3000);
  };

  const handleAddShippingCost = async () => {
    if (minQuantity === '' || shippingCost === '') {
      showNotification('Por favor complete todos los campos del costo de envío.');
      return;
    }

    setIsLoading(true);
    try {
      const newShippingCost = { minQuantity: Number(minQuantity), shippingCost: Number(shippingCost) };
      await addDoc(collection(db, 'shippingCosts'), newShippingCost);
      setShippingCosts([...shippingCosts, newShippingCost]);
      setMinQuantity('');
      setShippingCost('');
      setIsLoading(false);
      showNotification('Costo de envío agregado exitosamente.');
    } catch (error) {
      setIsLoading(false);
      showNotification('Error al agregar el costo de envío.');
      console.error("Error adding shipping cost: ", error);
    }
  };

  const handleAddShippingDiscount = async () => {
    if (minAmount === '' || shippingDiscount === '') {
      showNotification('Por favor complete todos los campos del descuento de envío.');
      return;
    }

    setIsLoading(true);
    try {
      const newShippingDiscount = { 
        minAmount: Number(minAmount), 
        shippingDiscount: Number(shippingDiscount),
        type: 'amount' // Para identificar que es un descuento por monto
      };
      await addDoc(collection(db, 'shippingDiscounts'), newShippingDiscount);
      setShippingDiscounts([...shippingDiscounts, newShippingDiscount]);
      setMinAmount('');
      setShippingDiscount('');
      setIsLoading(false);
      showNotification('Descuento de envío agregado exitosamente.');
    } catch (error) {
      setIsLoading(false);
      showNotification('Error al agregar el descuento de envío.');
      console.error("Error adding shipping discount: ", error);
    }
  };

  const handleDeleteShippingCost = async (id) => {
    setIsLoading(true);
    try {
      await deleteDoc(doc(db, 'shippingCosts', id));
      setShippingCosts(shippingCosts.filter(cost => cost.id !== id));
      setIsLoading(false);
      showNotification('Costo de envío eliminado exitosamente.');
    } catch (error) {
      setIsLoading(false);
      showNotification('Error al eliminar el costo de envío.');
      console.error("Error deleting shipping cost: ", error);
    }
  };

  const handleDeleteShippingDiscount = async (id) => {
    setIsLoading(true);
    try {
      await deleteDoc(doc(db, 'shippingDiscounts', id));
      setShippingDiscounts(shippingDiscounts.filter(discount => discount.id !== id));
      setIsLoading(false);
      showNotification('Descuento de envío eliminado exitosamente.');
    } catch (error) {
      setIsLoading(false);
      showNotification('Error al eliminar el descuento de envío.');
      console.error("Error deleting shipping discount: ", error);
    }
  };

  const handleFormTypeChange = (e) => {
    const value = e.target.value;
    setFormType(value);
    setMinQuantity('');
    setShippingCost('');
    setMinAmount('');
    setShippingDiscount('');
  };

  const handleAdd = () => {
    if (formType === 'cost') handleAddShippingCost();
    else if (formType === 'discount') handleAddShippingDiscount();
    else showNotification('Elige qué quieres agregar en el selector.');
  };

  return (
    <div className="shipping-cost-config">
      <Notification message={notification.message} show={notification.show} />
      {isLoading && <Spinner />}
      <h2 className="shipping-cost-config-title">Configuración de Costos y Descuentos de Envío</h2>

      {/* Un solo formulario con select para elegir tipo */}
      <div className="shipping-form">
        <h3 className="shipping-form-title">Agregar regla de envío</h3>
        <div className="shipping-form-row">
          <div className="shipping-field shipping-field-full">
            <label htmlFor="shipping-type">Tipo de regla</label>
            <select
              id="shipping-type"
              value={formType}
              onChange={handleFormTypeChange}
              className="shipping-cost-input"
            >
              <option value="">Seleccionar tipo</option>
              <option value="cost">Costo de envío básico (por cantidad mínima)</option>
              <option value="discount">Descuento de envío por monto (ej. envío gratis)</option>
            </select>
          </div>
        </div>

        {formType === 'cost' && (
          <div className="shipping-form-row">
            <div className="shipping-field">
              <label htmlFor="shipping-min-quantity">Cantidad mínima</label>
              <input
                id="shipping-min-quantity"
                type="number"
                min="1"
                placeholder="Ej: 5"
                value={minQuantity}
                onChange={(e) => setMinQuantity(e.target.value)}
                className="shipping-cost-input"
              />
            </div>
            <div className="shipping-field">
              <label htmlFor="shipping-cost">Costo de envío ($)</label>
              <input
                id="shipping-cost"
                type="number"
                min="0"
                placeholder="Ej: 500"
                value={shippingCost}
                onChange={(e) => setShippingCost(e.target.value)}
                className="shipping-cost-input"
              />
            </div>
          </div>
        )}

        {formType === 'discount' && (
          <div className="shipping-form-row">
            <div className="shipping-field">
              <label htmlFor="shipping-min-amount">Monto mínimo de compra ($)</label>
              <input
                id="shipping-min-amount"
                type="number"
                min="0"
                placeholder="Ej: 5000"
                value={minAmount}
                onChange={(e) => setMinAmount(e.target.value)}
                className="shipping-cost-input"
              />
            </div>
            <div className="shipping-field">
              <label htmlFor="shipping-discount">Descuento ($) — 0 = envío gratis</label>
              <input
                id="shipping-discount"
                type="number"
                min="0"
                placeholder="Ej: 0 o 200"
                value={shippingDiscount}
                onChange={(e) => setShippingDiscount(e.target.value)}
                className="shipping-cost-input"
              />
            </div>
          </div>
        )}

        {formType && (
          <div className="shipping-form-actions">
            <button
              type="button"
              onClick={handleAdd}
              className={formType === 'cost' ? 'shipping-cost-button' : 'shipping-discount-button'}
            >
              {formType === 'cost' ? 'Agregar costo de envío' : 'Agregar descuento de envío'}
            </button>
          </div>
        )}
      </div>

      {/* Lista de costos de envío básicos */}
      <div className="shipping-cost-list">
        <h3 className="shipping-cost-list-title">Costos de envío básicos</h3>
        {shippingCosts.length === 0 ? (
          <p className="shipping-list-empty">No hay costos de envío configurados. Agrega uno desde el formulario.</p>
        ) : (
          shippingCosts.map(cost => (
            <div key={cost.id} className="shipping-cost-item">
              <span className="shipping-item-badge">Costo básico</span>
              <div className="shipping-item-details">
                <span className="shipping-cost-detail"><strong>{cost.minQuantity}</strong> unidades mín.</span>
                <span className="shipping-cost-detail"><strong>${cost.shippingCost}</strong></span>
              </div>
              <div className="shipping-item-actions">
                <button type="button" onClick={() => handleDeleteShippingCost(cost.id)} className="shipping-cost-delete-button" aria-label="Eliminar costo de envío">
                  Eliminar
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Lista de descuentos de envío */}
      <div className="shipping-discount-list">
        <h3 className="shipping-discount-list-title">Descuentos de envío por monto</h3>
        {shippingDiscounts.length === 0 ? (
          <p className="shipping-list-empty">No hay descuentos de envío configurados. Agrega uno desde el formulario.</p>
        ) : (
          shippingDiscounts.map(discount => (
            <div key={discount.id} className="shipping-discount-item">
              <span className="shipping-item-badge">Por monto</span>
              <div className="shipping-item-details">
                <span className="shipping-discount-detail">Mín. <strong>${discount.minAmount}</strong></span>
                <span className="shipping-discount-detail">
                  {discount.shippingDiscount === 0 ? 'Envío gratis' : <><strong>${discount.shippingDiscount}</strong> desc.</>}
                </span>
              </div>
              <div className="shipping-item-actions">
                <button type="button" onClick={() => handleDeleteShippingDiscount(discount.id)} className="shipping-discount-delete-button" aria-label="Eliminar descuento de envío">
                  Eliminar
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default CostoEnvio;
